// src/components/watchlist/LocationSearch.js
import React, { useState, useEffect } from 'react'
import {
  Box,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  Paper,
  Typography,
  CircularProgress,
} from '@mui/material'
import { Search as SearchIcon, LocationOn as LocationOnIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

const LocationSearch = ({ onLocationSelected }) => {
  const { t } = useTranslation()

  const [searchQuery, setSearchQuery] = useState('')
  const [searchSuggestions, setSearchSuggestions] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Debounced search with Nominatim
  useEffect(() => {
    if (searchQuery.length < 3) {
      setSearchSuggestions([])
      return
    }

    const controller = new AbortController()
    const signal = controller.signal

    const timer = setTimeout(async () => {
      setLoading(true)
      setError(null)

      try {
        // Use Nominatim API to search for locations
        const url = `https://nominatim.openstreetmap.org/search?format=json&countrycodes=nl&limit=5&q=${encodeURIComponent(
          searchQuery
        )}`
        const response = await fetch(url, { signal })

        if (!response.ok) {
          throw new Error(`Error fetching suggestions: ${response.status}`)
        }

        const data = await response.json()
        setSearchSuggestions(data)
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error fetching search suggestions:', err)
          setError(err.message)
        }
      } finally {
        setLoading(false)
      }
    }, 500)

    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [searchQuery])

  const handleSelectSuggestion = async (suggestion) => {
    // Clear suggestions and set the search query
    setSearchQuery(suggestion.display_name)
    setSearchSuggestions([])

    try {
      // Get detailed information about the selected location
      const osmPrefix = { node: 'N', way: 'W', relation: 'R' }
      let osmId = ''

      if (suggestion.osm_type && suggestion.osm_id) {
        osmId = `${osmPrefix[suggestion.osm_type]}${suggestion.osm_id}`
      }

      // Only perform lookup if we have a valid OSM ID
      if (osmId) {
        const lookupUrl = `https://nominatim.openstreetmap.org/lookup?osm_ids=${osmId}&format=json&addressdetails=1`
        const response = await fetch(lookupUrl)

        if (!response.ok) {
          throw new Error(`Error fetching location details: ${response.status}`)
        }

        const details = await response.json()

        if (Array.isArray(details) && details.length > 0) {
          const place = details[0]
          const addressData = place.address || {}

          onLocationSelected({
            address: place.display_name || suggestion.display_name,
            country: addressData.country || 'Netherlands',
            postcode: addressData.postcode || '',
            osm_data: {
              type: 'point',
              properties: {
                lat: parseFloat(place.lat || suggestion.lat),
                lon: parseFloat(place.lon || suggestion.lon),
                display_name: place.display_name || suggestion.display_name,
              },
            },
          })
          return
        }
      }

      // Fallback if lookup fails
      onLocationSelected({
        address: suggestion.display_name,
        country: 'Netherlands',
        postcode: '',
        osm_data: {
          type: 'point',
          properties: {
            lat: parseFloat(suggestion.lat),
            lon: parseFloat(suggestion.lon),
            display_name: suggestion.display_name,
          },
        },
      })
    } catch (err) {
      console.error('Error fetching location details:', err)

      // Fallback with basic suggestion data
      onLocationSelected({
        address: suggestion.display_name,
        country: 'Netherlands',
        postcode: '',
        osm_data: {
          type: 'point',
          properties: {
            lat: parseFloat(suggestion.lat),
            lon: parseFloat(suggestion.lon),
            display_name: suggestion.display_name,
          },
        },
      })
    }
  }

  return (
    <Box sx={{ mb: 3 }}>
      <FormControl fullWidth variant="outlined">
        <InputLabel htmlFor="location-search">{t('notifications.searchPlaceholder')}</InputLabel>
        <OutlinedInput
          id="location-search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          startAdornment={
            <InputAdornment position="start">
              {loading ? <CircularProgress size={20} /> : <SearchIcon />}
            </InputAdornment>
          }
          label={t('notifications.searchPlaceholder')}
          placeholder={t('notifications.searchHint', 'Enter city, neighborhood, street...')}
        />
      </FormControl>

      {error && (
        <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
          {error}
        </Typography>
      )}

      {searchSuggestions.length > 0 && (
        <Paper sx={{ mt: 1, maxHeight: 300, overflow: 'auto' }}>
          {searchSuggestions.map((suggestion) => (
            <Box
              key={suggestion.place_id}
              sx={{
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:hover': { bgcolor: 'action.hover' },
                '&:last-child': { borderBottom: 'none' },
              }}
              onClick={() => handleSelectSuggestion(suggestion)}
            >
              <LocationOnIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Box>
                <Typography variant="body2">{suggestion.display_name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {suggestion.type}: {suggestion.osm_type} {suggestion.osm_id}
                </Typography>
              </Box>
            </Box>
          ))}
        </Paper>
      )}

      {searchQuery.length >= 3 && !loading && searchSuggestions.length === 0 && (
        <Paper sx={{ mt: 1, p: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {t('notifications.noResults', 'No locations found. Try a different search term.')}
          </Typography>
        </Paper>
      )}
    </Box>
  )
}

export default LocationSearch
